import React, { useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { MenuOutlined } from "@ant-design/icons";
import Sidebar from "../../components/Sidebar";
import useIsPhone from "../../hooks/useIsPhone";
import useSmoothScroll from "../../motion/useSmoothScroll";
import { ReactComponent as LogoIcon } from "../../assets/appinstalls-mark.svg";
import styles from "./styles.module.scss";

const COLLAPSED_KEY = "promobileAdmin:sidebarCollapsed";

const readCollapsed = () => {
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
};

// The client's app shell: a sidebar card and a content column on the page
// background, 16px apart and 16px from the window edges. On a phone the
// sidebar leaves the row: a top bar opens it as a drawer over the page.
const MainLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [menuOpen, setMenuOpen] = useState(false);
  const isPhone = useIsPhone();
  const { pathname } = useLocation();
  const contentRef = useRef<HTMLElement>(null);
  // On a desktop the content column scrolls, and the wheel glides over it.
  // A phone scrolls the whole page by touch, natively.
  const lenisRef = useSmoothScroll(contentRef, !isPhone);

  // A picked page, or growing out of phone size, puts the drawer away.
  useEffect(() => setMenuOpen(false), [pathname, isPhone]);

  // Another screen opens at its top, not where the last one was left.
  useEffect(() => {
    if (lenisRef.current) {
      lenisRef.current.scrollTo(0, { immediate: true, force: true });
    } else if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
    if (isPhone) window.scrollTo(0, 0);
    // Only a new screen resets it, not a phone/desktop switch.
  }, [pathname]);

  const toggle = () => {
    setCollapsed((prev) => {
      try {
        window.localStorage.setItem(COLLAPSED_KEY, prev ? "0" : "1");
      } catch {
        // the choice just won't survive a reload
      }
      return !prev;
    });
  };

  return (
    <div className={styles.layout}>
      {isPhone && (
        <header className={styles.topBar}>
          <button
            type="button"
            className={styles.menuButton}
            onClick={() => setMenuOpen(true)}
            aria-label="Open menu"
            aria-expanded={menuOpen}
          >
            <MenuOutlined />
          </button>
          <LogoIcon className={styles.logo} />
          <span className={styles.logoLabel}>Admin</span>
        </header>
      )}
      <Sidebar
        opened={isPhone || !collapsed}
        onToggle={toggle}
        drawer={
          isPhone
            ? { open: menuOpen, onClose: () => setMenuOpen(false) }
            : undefined
        }
      />
      <main ref={contentRef} className={styles.content}>
        <Outlet />
      </main>
    </div>
  );
};

export default MainLayout;
